import type { InputFrame } from "../game/input";

/** Which controls the help and the on-screen hints name (#147): the input you're using, not all of them. */
export type InputKind = InputFrame["source"];

export const INPUT_KINDS: readonly InputKind[] = ["keyboard", "gamepad", "touch"];
export const INPUT_LABELS: Record<InputKind, string> = { keyboard: "Keyboard", gamepad: "Gamepad", touch: "Touch" };

/**
 * The input to name: the pad once it's driven the skater, the touch controls
 * when they're what you skate with, otherwise the keyboard. `source` is the
 * game's sticky last-used input, so this switches live with a key or a button.
 */
export function activeInput(source: InputKind, onGlass: boolean): InputKind {
  if (source === "gamepad") return "gamepad";
  return source === "touch" || onGlass ? "touch" : "keyboard";
}

/** A pad button the hints name. */
export type PadButton = "✕" | "□" | "○" | "△" | "L1" | "R1" | "L2" | "R2" | "Start" | "Select";

/** The default keys and the touch buttons for each pad button (`input.ts`, `TouchControls.tsx`). */
const KEY_FOR: Record<PadButton, string> = { "✕": "Space", "□": "J", "○": "K", "△": "L", L1: "Q", R1: "E", L2: "U", R2: "O", Start: "Esc", Select: "H" };
const TOUCH_FOR: Record<PadButton, string> = { "✕": "Ollie", "□": "Flip", "○": "Grab", "△": "Grind", L1: "L", R1: "R", L2: "Nollie", R2: "Revert", Start: "❚❚", Select: "?" };

/** One button's name on `input`: the pad glyph, its default key, or the touch button. */
export function buttonFor(input: InputKind, button: PadButton): string {
  return input === "gamepad" ? button : input === "keyboard" ? KEY_FOR[button] : TOUCH_FOR[button];
}

const PAD_WORDS = /L1|R1|L2|R2|✕|□|○|△/g;

/** A line written with pad buttons ("let go of △"), reworded with the buttons of `input`. */
export function namedFor(input: InputKind, text: string): string {
  return input === "gamepad" ? text : text.replace(PAD_WORDS, (b) => buttonFor(input, b as PadButton));
}
